// fn-2.2 (2026-05-17) — Files SPA page.
//
// Surfaces the caller's workspace-scoped files (account-scoped under
// the new fn-2 schema). Lists, lets you upload a new file, and lets
// you archive (soft-delete) an existing one. Mount-on-new-session is
// driven from the Agents page; this page is the durable view.
//
// **No `<Tailwind>`-style component library**: matches the existing
// Anthropic-Console-inspired CSS approach — bespoke markup with
// `--sb-*` tokens. The list shape mirrors `pages/runs/list.tsx` for
// consistency.
//
// **Upload path**: `POST /api/multipart/files` (a sibling REST endpoint
// on the BFF, not tRPC) carries the multipart body to the control
// plane. On success we invalidate the tRPC `files.list` query so the
// new file appears within one polling tick.
//
// **Cross-origin (fn-45.4):** in deployed mode the SPA + BFF are on
// separate origins, so the upload must target the absolute
// `${VITE_BFF_URL}/api/multipart/files` (`bffEndpoints().uploadUrl`) — a
// relative path would post to the static SPA origin (no BFF, no `__Host-`
// cookies, no CSRF preHandler). Dev keeps the relative path via the Vite
// proxy.

import { useRef, useState } from 'react';

import { bffEndpoints } from '../../auth/bff-origin.js';
import { retryableRefreshFetch, trpc } from '../../trpc-client.js';
import { formatTime } from '../format.js';

const POLL_MS = 3_000;

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

export function FilesListPage(): React.ReactElement {
  const query = trpc.files.list.useQuery({ limit: 50 }, { refetchInterval: POLL_MS });
  const utils = trpc.useUtils();
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const archiveMutation = trpc.files.archive.useMutation();

  const onUpload = async (file: File): Promise<void> => {
    setUploadError(null);
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file, file.name);
      // fn-45.4 — wrap with the one-shot retryable-refresh retry so a
      // benign multi-instance refresh race (503 SESSION_REFRESH_RETRY)
      // doesn't fail the upload; carry the CSRF custom header the BFF
      // preHandler requires on unsafe methods (a cross-site form can't set it).
      const res = await retryableRefreshFetch(globalThis.fetch.bind(globalThis))(
        bffEndpoints().uploadUrl,
        {
          method: 'POST',
          body: form,
          credentials: 'include',
          headers: { 'X-Requested-With': 'fetch' },
        },
      );
      if (!res.ok) {
        let msg = `Upload failed (${res.status})`;
        try {
          const body = (await res.json()) as { message?: string; error?: string };
          if (body.message) msg = body.message;
          else if (body.error) msg = body.error;
        } catch {
          // ignore parse failures
        }
        setUploadError(msg);
        return;
      }
      await utils.files.list.invalidate();
    } catch (err) {
      setUploadError((err as Error).message ?? 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const onArchive = async (fileId: string): Promise<void> => {
    try {
      await archiveMutation.mutateAsync({ fileId });
      await utils.files.list.invalidate();
    } catch {
      // surfaces via archiveMutation.error
    }
  };

  if (query.isPending) {
    return (
      <section className="page page-files" aria-busy="true">
        <h1>Files</h1>
        <p>Loading files…</p>
      </section>
    );
  }
  if (query.error !== null) {
    return (
      <section className="page page-files" role="alert">
        <h1>Files</h1>
        <p>Failed to load files: {query.error.message}</p>
      </section>
    );
  }

  const files = query.data?.data ?? [];

  return (
    <section className="page page-files">
      <header
        className="files-header"
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: '1rem',
        }}
      >
        <div>
          <h1>Files</h1>
          <p style={{ color: 'var(--sb-text-muted)', margin: '0.25rem 0 0' }}>
            Upload a CLAUDE.md to give your agents user context. Files here are workspace-scoped —
            mount on as many sessions as you like.
          </p>
        </div>
        <label className="files-upload-btn" style={{ alignSelf: 'flex-start' }}>
          <input
            ref={fileInputRef}
            type="file"
            style={{ display: 'none' }}
            disabled={uploading}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void onUpload(f);
            }}
          />
          <span
            className="sb-chip"
            style={{
              cursor: uploading ? 'not-allowed' : 'pointer',
              background: 'var(--sb-text)',
              color: 'var(--sb-bg)',
              padding: '0.4rem 0.85rem',
              borderRadius: '6px',
              fontWeight: 500,
              opacity: uploading ? 0.6 : 1,
            }}
          >
            {uploading ? 'Uploading…' : 'Upload file'}
          </span>
        </label>
      </header>

      <p
        style={{ color: 'var(--sb-text-subtle)', margin: '0.85rem 0 0.25rem', fontSize: '0.78rem' }}
      >
        ⚠ Do not paste secrets — file content persists in MA event history forever once mounted on a
        session.
      </p>

      {uploadError !== null && (
        <p role="alert" style={{ color: 'var(--sb-status-failed)', marginTop: '0.5rem' }}>
          {uploadError}
        </p>
      )}

      {files.length === 0 ? (
        <div
          className="files-empty"
          style={{ padding: '2rem 0', textAlign: 'center', color: 'var(--sb-text-muted)' }}
        >
          <strong style={{ display: 'block', fontSize: '1.05rem', color: 'var(--sb-text)' }}>
            No files yet
          </strong>
          <p style={{ marginTop: '0.5rem' }}>
            Upload a CLAUDE.md to give your agents user context.
          </p>
        </div>
      ) : (
        <table className="sb-table" style={{ marginTop: '0.85rem' }}>
          <thead>
            <tr>
              <th>Name</th>
              <th>Size</th>
              <th>State</th>
              <th>ID</th>
              <th>Uploaded</th>
              <th style={{ width: '8rem' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {files.map((f) => {
              const filename = f.metadata?.filename ?? f.s3_key.split('/').pop() ?? f.id;
              const isArchived = f.state === 'archived';
              return (
                <tr key={f.id} style={isArchived ? { opacity: 0.55 } : undefined}>
                  <td style={{ fontWeight: 500 }}>{String(filename)}</td>
                  <td>{formatBytes(f.size_bytes)}</td>
                  <td>
                    <span className="sb-chip" style={{ fontSize: '0.72rem' }}>
                      {f.state}
                    </span>
                  </td>
                  <td
                    className="sb-cell-id"
                    title={f.id}
                    style={{
                      fontFamily: 'var(--sb-font-mono)',
                      fontSize: '0.82rem',
                      color: 'var(--sb-text-muted)',
                    }}
                  >
                    {f.id.slice(0, 12)}…
                  </td>
                  <td style={{ color: 'var(--sb-text-muted)', fontSize: '0.85rem' }}>
                    {formatTime(f.uploaded_at)}
                  </td>
                  <td>
                    {!isArchived && (
                      <button
                        type="button"
                        disabled={archiveMutation.isPending}
                        onClick={() => void onArchive(f.id)}
                        className="files-archive-btn"
                        style={{
                          appearance: 'none',
                          background: 'transparent',
                          border: '1px solid var(--sb-border)',
                          color: 'var(--sb-text-muted)',
                          borderRadius: '4px',
                          padding: '0.25rem 0.65rem',
                          fontSize: '0.78rem',
                          cursor: archiveMutation.isPending ? 'not-allowed' : 'pointer',
                        }}
                      >
                        {archiveMutation.isPending ? 'Archiving…' : 'Archive'}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {archiveMutation.error !== null && (
        <p
          role="alert"
          style={{ color: 'var(--sb-status-failed)', marginTop: '0.5rem', fontSize: '0.85rem' }}
        >
          Archive failed: {archiveMutation.error.message}
        </p>
      )}
    </section>
  );
}
